import { z } from "zod";

/**
 * Storefront chat (boxed chat v2, scoped to one environment). A room is identified by its id
 * within the caller's environment; the environment is never on the wire, it is the caller's.
 * Adding a room is a member here: nothing stored is keyed on the list.
 */
export const CHAT_ROOM_IDS = ["general"] as const;
export type ChatRoomId = (typeof CHAT_ROOM_IDS)[number];

export function isChatRoomId(value: string): value is ChatRoomId {
  return (CHAT_ROOM_IDS as readonly string[]).includes(value);
}

/** Longest message, in UTF-16 code units after the strip and trim (boxed's `MAX_CHAT_MESSAGE_LENGTH`). */
export const MAX_CHAT_MESSAGE_LENGTH = 200;
/** How many messages a room's history returns (boxed's `CHAT_HISTORY_LIMIT`). */
export const CHAT_HISTORY_LIMIT = 120;
/** Longest report description and mute reason. */
export const MAX_CHAT_NOTE_LENGTH = 200;

export const CHAT_REPORT_CATEGORIES = ["HARASSING", "INAPPROPRIATE", "SPAM", "SUSPICIOUS"] as const;
export type ChatReportCategory = (typeof CHAT_REPORT_CATEGORIES)[number];

/** Why a message was refused before it was stored: `details.reason` on `chat_message_rejected`. */
export const CHAT_REJECT_REASONS = ["empty", "too_long", "profanity", "link"] as const;
export type ChatRejectReason = (typeof CHAT_REJECT_REASONS)[number];

/** The quoted parent of a reply, snapshotted at send. `deleted` once the parent is removed (text dropped). */
export const chatReplyToSchema = z.object({
  messageId: z.string(),
  endUserId: z.string(),
  displayName: z.string(),
  text: z.string().optional(),
  deleted: z.boolean().optional(),
});
export type ChatReplyTo = z.infer<typeof chatReplyToSchema>;

/**
 * A chat message as shoppers see it. Built from an allowlist (`toChatMessage`), never the stored
 * item: who deleted it and the tenant/environment ids never leave the server. A deleted message
 * keeps its place in history with `deleted: true` and no text.
 */
export const chatMessageSchema = z.object({
  messageId: z.string(),
  roomId: z.string(),
  endUserId: z.string(),
  /** The sender's username when the message was sent. */
  displayName: z.string(),
  text: z.string().optional(),
  replyTo: chatReplyToSchema.optional(),
  deleted: z.boolean(),
  /** ISO 8601. */
  createdAt: z.string(),
});
export type ChatMessage = z.infer<typeof chatMessageSchema>;

/** GET /v1/chat/rooms/{roomId}/messages. `viewer` only with a session. */
export const chatHistoryResponseSchema = z.object({
  roomId: z.string(),
  messages: z.array(chatMessageSchema),
  /** Shoppers who sent a heartbeat in the last two minutes. */
  headcount: z.int(),
  viewer: z
    .object({
      muted: z.boolean(),
      /** ISO 8601; absent when not muted or muted indefinitely. */
      mutedUntil: z.string().optional(),
    })
    .optional(),
});
export type ChatHistoryResponse = z.infer<typeof chatHistoryResponseSchema>;

export const sendChatMessageBodySchema = z.object({
  text: z.string().max(2000),
  replyToMessageId: z.string().max(64).optional(),
});
export type SendChatMessageBody = z.infer<typeof sendChatMessageBodySchema>;

export const sendChatMessageResponseSchema = z.object({ message: chatMessageSchema });

export const reportChatMessageBodySchema = z.object({
  category: z.enum(CHAT_REPORT_CATEGORIES),
  description: z.string().trim().max(MAX_CHAT_NOTE_LENGTH).optional(),
});
export type ReportChatMessageBody = z.infer<typeof reportChatMessageBodySchema>;

export const reportChatMessageResponseSchema = z.object({ reportId: z.string() });

export const chatHeartbeatResponseSchema = z.object({ headcount: z.int() });

/** The realtime namespace; one channel per room: `/chat/{environmentId}/{roomId}`. */
export const CHAT_CHANNEL_NAMESPACE = "chat";
export function chatChannel(environmentId: string, roomId: string): string {
  return `/${CHAT_CHANNEL_NAMESPACE}/${environmentId}/${roomId}`;
}

/** What the platform publishes on a room's channel. */
export const chatEventSchema = z.discriminatedUnion("type", [
  z.object({ type: z.literal("message"), message: chatMessageSchema, headcount: z.int() }),
  z.object({ type: z.literal("message-deleted"), roomId: z.string(), messageId: z.string() }),
  z.object({ type: z.literal("presence"), roomId: z.string(), headcount: z.int() }),
]);
export type ChatEvent = z.infer<typeof chatEventSchema>;

/** Checks an incoming realtime payload; null for anything this client does not understand. */
export function parseChatEvent(payload: unknown): ChatEvent | null {
  const parsed = chatEventSchema.safeParse(payload);
  return parsed.success ? parsed.data : null;
}
